/**
 * O selo de status do SM.
 *
 * Papel: reproduz `.badge` e as cinco classes de estado da origem (l. 337–351):
 * mono 10 px 500, cápsula, fundo na cor do estado a 15 %.
 *
 * Como usar: o tom de cada status vem de `TOM_DO_SELO_CORRETIVA` e
 * `TOM_DO_SELO_PREVENTIVA`, em `rotulos.ts`; o texto, dos `ROTULOS_STATUS_*`.
 * Os três tons cobrem as cinco classes da origem, que repetem cor entre
 * corretiva e preventiva. O `.badge.atrasado` da origem nunca é usado e não tem
 * tom aqui.
 */
import { type ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';

/** As três cores de selo que a origem usa. */
export type TomDoSelo = 'verde' | 'amarelo' | 'laranja';

/** O texto e o fundo de cada tom. */
const CLASSES_DO_TOM: Readonly<Record<TomDoSelo, string>> = {
  verde: 'bg-sucesso/15 text-sucesso',
  amarelo: 'bg-emb-amarelo/15 text-emb-amarelo',
  laranja: 'bg-aviso/15 text-aviso',
};

/**
 * Um selo de status.
 *
 * @param tom - A cor do estado.
 * @param children - O rótulo do status, como a origem o escreve.
 * @returns O selo.
 */
export function Selo({ tom, children }: { tom: TomDoSelo; children: ReactNode }) {
  return (
    <span
      className={cn(
        'inline-block rounded-[20px] px-[9px] py-0.5 font-mono text-[10px] font-medium tracking-[0.2px] whitespace-nowrap',
        CLASSES_DO_TOM[tom],
      )}
    >
      {children}
    </span>
  );
}
